import { useEffect, useRef, useState } from 'react';
import emailjs from '@emailjs/browser';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const EMAILJS_SERVICE_ID  = 'service_gnx5var';
const EMAILJS_TEMPLATE_ID = 'template_ai9yb6c';
const EMAILJS_PUBLIC_KEY  = 'UV0C0YD0Ovb0THOV0';
const COOLDOWN_MS = 3 * 60 * 1000;

const NAV_ITEMS = [
  { id: 'inicio',   label: 'Início',   icon: 'ri-home-4-line' },
  { id: 'sobre',    label: 'Sobre',    icon: 'ri-information-line' },
  { id: 'servicos', label: 'Serviços', icon: 'ri-sparkling-line' },
  { id: 'contato',  label: 'Contato',  icon: 'ri-phone-line' },
];

const HEALTH_SERVICES = [
  {
    icon: 'ri-brain-line',
    title: 'Psiquiatra',
    description: 'Especialista em saúde mental, diagnóstico e tratamento de transtornos psiquiátricos com abordagem humanizada e personalizada.',
  },
  {
    icon: 'ri-heart-pulse-line',
    title: 'Psicólogo',
    description: 'Atendimento psicológico especializado para bem-estar emocional, desenvolvimento pessoal e qualidade de vida.',
  },
  {
    icon: 'ri-leaf-line',
    title: 'Nutricionista',
    description: 'Planejamento nutricional individualizado para saúde, performance e equilíbrio alimentar com acompanhamento contínuo.',
  },
  {
    icon: 'ri-flask-line',
    title: 'Nutróloga',
    description: 'Cuidado médico especializado em nutrição, metabolismo e emagrecimento, com foco em saúde e longevidade.',
  },
  {
    icon: 'ri-sparkling-line',
    title: 'Esteticista',
    description: 'Tratamentos estéticos faciais e corporais de alta performance para realçar sua beleza natural com técnicas modernas.',
  },
];

const DIFFERENTIALS = [
  {
    icon: 'ri-heart-3-line',
    title: 'Atendimento Humanizado',
    description: 'Cuidado personalizado e acolhedor, respeitando a individualidade de cada paciente.',
  },
  {
    icon: 'ri-award-line',
    title: 'Profissionais Qualificados',
    description: 'Equipe multidisciplinar altamente capacitada e em constante atualização para oferecer o melhor cuidado aos pacientes.',
  },
  {
    icon: 'ri-vip-diamond-line',
    title: 'Ambiente Sofisticado',
    description: 'Instalações premium projetadas para proporcionar conforto, privacidade e uma experiência única.',
  },
  {
    icon: 'ri-lightbulb-flash-line',
    title: 'Tecnologia Moderna',
    description: 'Equipamentos de última geração e protocolos inovadores para resultados superiores e tratamentos eficazes.',
  },
];

const VALUES_LIST = [
  'Ética e responsabilidade profissional.',
  'Respeito a individualidade de cada pessoa.',
  'Compromisso com inovação e evolução profissional.',
  'Atendimento humanizado.',
  'Excelência técnica e científica.',
  'Resultados naturais e seguros.',
];

const TREATMENTS = [
  { icon: 'ri-sparkling-2-line', title: 'Rejuvenescimento' },
  { icon: 'ri-flashlight-line',  title: 'Flacidez' },
  { icon: 'ri-drop-line',        title: 'Manchas' },
  { icon: 'ri-eye-line',         title: 'Cicatrizes' },
  { icon: 'ri-magic-line',       title: 'Rugas' },
  { icon: 'ri-fire-line',        title: 'Emagrecimento' },
  { icon: 'ri-body-scan-line',   title: 'Gordura Localizada' },
  { icon: 'ri-leaf-line',        title: 'Celulites' },
];

const MISSION_ITEMS = [
  {
    title: 'Missão',
    text: 'Promover saúde, autoestima e qualidade de vida por meio de um atendimento integrado, humano e personalizado. Unimos estética avançada, saúde integrada e bem-estar para oferecer tratamentos seguros, naturais e eficazes, respeitando a individualidade de cada paciente e buscando resultados que valorizam a beleza, o equilíbrio e o bem-estar.',
  },
  {
    title: 'Visão',
    text: 'Ser referência em estética avançada, saúde e bem-estar, reconhecida pela excelência profissional, pelo cuidado humanizado e pelos resultados que transformam vidas. Nosso objetivo é ir além da estética, oferecendo soluções completas, que cuidem da beleza, da saúde física e do equilíbrio emocional.',
  },
];

type FormStatus = 'idle' | 'sending' | 'ok' | 'error' | 'cooldown';

export default function HomePage() {
  const [scrolled, setScrolled]               = useState(false);
  const [scrollProgress, setScrollProgress]   = useState(0);
  const [menuOpen, setMenuOpen]               = useState(false);
  const [formStatus, setFormStatus]           = useState<FormStatus>('idle');
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const formRef     = useRef<HTMLFormElement>(null);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useScrollAnimation();

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setScrolled(scrollTop > 60);
      setScrollProgress(docHeight > 0 ? Math.min((scrollTop / docHeight) * 100, 100) : 0);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const startCooldown = (remainingMs: number) => {
    let remaining = Math.ceil(remainingMs / 1000);
    setCooldownSeconds(remaining);
    setFormStatus('cooldown');
    cooldownRef.current = setInterval(() => {
      remaining -= 1;
      setCooldownSeconds(remaining);
      if (remaining <= 0) {
        if (cooldownRef.current) clearInterval(cooldownRef.current);
        setFormStatus('idle');
        setCooldownSeconds(0);
      }
    }, 1000);
  };

  useEffect(() => {
    const lastSent = localStorage.getItem('idb_last_contact_sent');
    if (lastSent) {
      const elapsed = Date.now() - parseInt(lastSent, 10);
      if (elapsed < COOLDOWN_MS) startCooldown(COOLDOWN_MS - elapsed);
    }
    return () => { if (cooldownRef.current) clearInterval(cooldownRef.current); };
  }, []);

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formRef.current) return;

    const lastSent = localStorage.getItem('idb_last_contact_sent');
    if (lastSent) {
      const elapsed = Date.now() - parseInt(lastSent, 10);
      if (elapsed < COOLDOWN_MS) { startCooldown(COOLDOWN_MS - elapsed); return; }
    }

    setFormStatus('sending');
    try {
      await emailjs.sendForm(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, formRef.current, EMAILJS_PUBLIC_KEY);
      localStorage.setItem('idb_last_contact_sent', Date.now().toString());
      setFormStatus('ok');
      formRef.current.reset();
      startCooldown(COOLDOWN_MS);
    } catch {
      setFormStatus('error');
    }
  };

  const fmtCooldown = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const navLinkStyle: React.CSSProperties = {
    color: scrolled ? '#111' : '#fff',
    border: '1px solid transparent',
    letterSpacing: '0.05em',
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Barra de progresso */}
      <div
        className="fixed top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#D4AF37] via-[#F4E5B8] to-[#D4AF37] z-50 transition-all duration-300"
        style={{ width: `${scrollProgress}%` }}
      />

      {/* Navegação */}
      <nav className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${scrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
        <div className="border-t border-[#D4AF37]" style={{ height: '1px' }} />
        <div className="px-4 md:px-6 py-4 md:py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 md:gap-3 min-w-0">
              <img
                src="https://static.readdy.ai/image/eec107a1548fe8d7ef2a6c045cb2b024/07f00f65d65583c55d51b986d8655c28.png"
                alt="Instituto Daiane Borges"
                className="h-9 md:h-12 w-auto flex-shrink-0"
              />
              <div className="h-6 md:h-8 w-[1px] bg-[#D4AF37] flex-shrink-0" />
              <span className={`font-serif text-sm md:text-xl font-bold transition-colors duration-300 truncate ${scrolled ? 'text-black' : 'text-white'}`}>
                Instituto Daiane Borges
              </span>
            </div>

            <div className="hidden md:flex items-center gap-1">
              {NAV_ITEMS.map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  className="relative flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium tracking-wide whitespace-nowrap cursor-pointer transition-all duration-300 group overflow-hidden"
                  style={navLinkStyle}
                  onMouseEnter={(e) => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.color      = '#D4AF37';
                    el.style.background = scrolled ? 'rgba(212,175,55,0.10)' : 'rgba(212,175,55,0.15)';
                    el.style.border     = '1px solid rgba(212,175,55,0.5)';
                    el.style.boxShadow  = '0 2px 16px rgba(212,175,55,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.color      = scrolled ? '#111' : '#fff';
                    el.style.background = 'transparent';
                    el.style.border     = '1px solid transparent';
                    el.style.boxShadow  = 'none';
                  }}
                >
                  <span className="w-4 h-4 flex items-center justify-center opacity-70 group-hover:opacity-100 transition-opacity duration-300">
                    <i className={`${item.icon} text-sm`} />
                  </span>
                  {item.label}
                  <span
                    className="absolute bottom-[6px] left-1/2 -translate-x-1/2 h-[1.5px] w-0 group-hover:w-3/5 transition-all duration-300 rounded-full"
                    style={{ background: 'linear-gradient(90deg, transparent, #D4AF37, transparent)' }}
                  />
                </a>
              ))}
            </div>

            <button
              className={`md:hidden flex-shrink-0 w-10 h-10 flex items-center justify-center cursor-pointer ${scrolled ? 'text-black' : 'text-white'}`}
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Menu"
            >
              <i className={`text-2xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
            </button>
          </div>

          {menuOpen && (
            <div
              className="md:hidden mt-0 relative overflow-hidden"
              style={{
                background:   'linear-gradient(135deg, #0a0a0a 0%, #1a1208 40%, #0f0d06 70%, #0a0a0a 100%)',
                borderTop:    '1px solid rgba(212,175,55,0.4)',
                borderBottom: '2px solid rgba(212,175,55,0.6)',
                boxShadow:    '0 8px 32px rgba(212,175,55,0.15), inset 0 1px 0 rgba(212,175,55,0.2)',
              }}
            >
              <div className="absolute inset-0 pointer-events-none">
                <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '180px', height: '180px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(212,175,55,0.12) 0%, transparent 70%)' }} />
                <div style={{ position: 'absolute', bottom: '-30px', left: '-30px', width: '140px', height: '140px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(212,175,55,0.08) 0%, transparent 70%)' }} />
              </div>

              <div className="flex items-center gap-2 px-6 pt-5 pb-3">
                <div style={{ width: '28px', height: '1px', background: 'linear-gradient(90deg, rgba(212,175,55,0.8), transparent)' }} />
                <span style={{ fontSize: '9px', letterSpacing: '3px', color: 'rgba(212,175,55,0.7)', fontWeight: 600, textTransform: 'uppercase' }}>Menu</span>
                <div style={{ flex: 1, height: '1px', background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.8))' }} />
              </div>

              <div className="flex flex-col px-4 pb-5 gap-1">
                {NAV_ITEMS.map((item) => (
                  <a
                    key={item.id}
                    href={`#${item.id}`}
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-4 px-4 py-3.5 rounded-xl cursor-pointer transition-all duration-300"
                    style={{ background: 'rgba(212,175,55,0.04)', border: '1px solid rgba(212,175,55,0.1)' }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.background  = 'rgba(212,175,55,0.12)';
                      (e.currentTarget as HTMLElement).style.borderColor = 'rgba(212,175,55,0.4)';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.background  = 'rgba(212,175,55,0.04)';
                      (e.currentTarget as HTMLElement).style.borderColor = 'rgba(212,175,55,0.1)';
                    }}
                  >
                    <div style={{ width: '36px', height: '36px', borderRadius: '10px', flexShrink: 0, background: 'linear-gradient(135deg, rgba(212,175,55,0.2), rgba(212,175,55,0.08))', border: '1px solid rgba(212,175,55,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <i className={`${item.icon} text-base`} style={{ color: '#D4AF37' }} />
                    </div>
                    <span style={{ fontSize: '15px', fontWeight: 600, color: '#FFFFFF', letterSpacing: '0.5px', flex: 1 }}>{item.label}</span>
                    <i className="ri-arrow-right-s-line text-base" style={{ color: 'rgba(212,175,55,0.5)' }} />
                  </a>
                ))}
              </div>

              <div className="px-6 pb-5 flex items-center justify-center gap-3">
                <div style={{ flex: 1, height: '1px', background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.3))' }} />
                <span style={{ fontSize: '10px', color: 'rgba(212,175,55,0.5)', letterSpacing: '2px', textTransform: 'uppercase' }}>Instituto Daiane Borges</span>
                <div style={{ flex: 1, height: '1px', background: 'linear-gradient(90deg, rgba(212,175,55,0.3), transparent)' }} />
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero */}
      <section id="inicio" className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/eec107a1548fe8d7ef2a6c045cb2b024/04054c30da7d3766b5ed4b961a0bd2ce.jpeg"
            alt="Instituto Daiane Borges"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50" />
        </div>
        <div className="relative z-10 text-center px-6 animate-fade-in w-full">
          <h1 className="font-serif text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-4 md:mb-6 tracking-tight leading-tight">
            Instituto Daiane Borges
          </h1>
          <div className="w-16 md:w-20 h-[2px] bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent mx-auto mb-6 md:mb-8" />
          <p className="text-lg sm:text-xl md:text-2xl text-white font-light mb-8 md:mb-12 tracking-wide">
            Excelência em Estética e Saúde
          </p>
        </div>
        <div className="absolute bottom-8 md:bottom-12 left-1/2 -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-2xl md:text-3xl text-white" />
        </div>
      </section>

      {/* Sobre */}
      <section id="sobre" className="py-16 md:py-32 px-4 md:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-5 gap-8 md:gap-16 items-center">
            <div className="md:col-span-3 order-2 md:order-1">
              <p data-animate="fade-up" className="text-xs font-semibold tracking-[3px] text-[#D4AF37] mb-4 uppercase">Sobre Nós</p>
              <h2 data-animate="fade-up" data-delay="100" className="font-serif text-3xl md:text-5xl font-bold text-black mb-4 md:mb-6 leading-tight">Instituto Daiane Borges</h2>
              <div data-animate="fade-up" data-delay="200" className="w-16 md:w-24 h-[2px] bg-[#D4AF37] mb-6 md:mb-8" />
              <p data-animate="fade-up" data-delay="300" className="text-sm md:text-base text-gray-700 leading-relaxed mb-4 md:mb-6">
                O Instituto Daiane Borges nasceu do sonho de criar um espaço onde saúde, bem-estar e beleza se encontram em perfeita harmonia. Com uma abordagem integrada e humanizada, oferecemos tratamentos de excelência que respeitam a individualidade de cada paciente.
              </p>
              <div className="space-y-4 md:space-y-6">
                {MISSION_ITEMS.map((item, i) => (
                  <div key={item.title} data-animate="fade-left" data-delay={`${(i + 1) * 150}`} className="bg-[#FAFAFA] border-l-[3px] border-[#D4AF37] p-4 md:p-6 rounded-r-lg">
                    <h3 className="font-semibold text-lg md:text-xl text-black mb-2">{item.title}</h3>
                    <p className="text-xs md:text-sm text-gray-600 leading-relaxed">{item.text}</p>
                  </div>
                ))}

                {/* Valores */}
                <div data-animate="fade-left" data-delay="450" className="bg-[#FAFAFA] border-l-[3px] border-[#D4AF37] p-4 md:p-6 rounded-r-lg">
                  <h3 className="font-semibold text-lg md:text-xl text-black mb-4">Valores</h3>
                  <ul className="space-y-2">
                    {VALUES_LIST.map((value) => (
                      <li key={value} className="flex items-start gap-3">
                        <span className="mt-[3px] flex-shrink-0 w-4 h-4 flex items-center justify-center">
                          <i className="ri-checkbox-blank-circle-fill text-[6px] text-[#D4AF37]" />
                        </span>
                        <span className="text-xs md:text-sm text-gray-600 leading-relaxed">{value}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div className="md:col-span-2 order-1 md:order-2">
              <div data-animate="fade-right" className="relative h-full">
                <img
                  src="https://static.readdy.ai/image/eec107a1548fe8d7ef2a6c045cb2b024/d0a0e52f0dc300b63c632fdb834fd6c0.jpeg"
                  alt="Ambiente do Instituto"
                  className="w-full h-64 sm:h-80 md:h-full md:min-h-[700px] object-cover rounded-2xl border border-[#D4AF37] shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Diferenciais */}
      <section className="py-16 md:py-32 px-4 md:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-16">
            <h2 data-animate="fade-up" className="font-serif text-3xl md:text-5xl font-bold text-black mb-4 md:mb-6 leading-tight">Nossos Diferenciais</h2>
            <div data-animate="fade" data-delay="200" className="w-16 md:w-24 h-[2px] bg-[#D4AF37] mx-auto" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-10">
            {DIFFERENTIALS.map((item, index) => (
              <div
                key={item.title}
                data-animate="fade-up"
                data-delay={`${index * 120}`}
                className="bg-white border border-[#D4AF37] rounded-2xl p-8 md:p-12 hover:border-2 hover:-translate-y-1 transition-all duration-300 group"
              >
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#F4E5B8] flex items-center justify-center mx-auto mb-6 md:mb-8 group-hover:scale-110 transition-transform duration-300">
                  <i className={`${item.icon} text-2xl md:text-3xl text-black`} />
                </div>
                <h3 className="font-semibold text-base md:text-lg text-black text-center mb-3 md:mb-5">{item.title}</h3>
                <p className="text-xs md:text-sm text-gray-600 leading-relaxed text-center">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Daiane Borges */}
      <section className="py-16 md:py-32 px-4 md:px-6 bg-[#F8F8F8]">
        <div className="max-w-7xl mx-auto">
          <div data-animate="zoom">
            <div className="relative bg-black rounded-2xl md:rounded-3xl overflow-hidden border border-[#D4AF37] shadow-2xl">
              <div className="absolute top-4 md:top-8 left-4 md:left-8 z-20">
                <span className="px-3 md:px-5 py-1.5 md:py-2 bg-[#D4AF37] text-black text-[10px] md:text-xs font-bold tracking-[2px] md:tracking-[3px] uppercase rounded-full">
                  ✦ DIRETORA&nbsp;&amp; Fundadora
                </span>
              </div>
              <div className="grid md:grid-cols-2 items-center">
                <div className="relative h-80 sm:h-[420px] md:h-[600px] overflow-hidden">
                  <img
                    src="https://static.readdy.ai/image/eec107a1548fe8d7ef2a6c045cb2b024/004239af197f8a44a8f6956611440ca4.jpeg"
                    alt="Daiane Borges"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: '50% 15%' }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-b md:bg-gradient-to-r from-transparent to-black/60" />
                </div>
                <div className="p-6 sm:p-8 md:p-16 relative z-10">
                  <p className="text-[10px] md:text-xs font-semibold tracking-[3px] md:tracking-[4px] text-[#D4AF37] mb-3 md:mb-4 uppercase">Diretora Fundadora</p>
                  <h3 className="font-serif text-3xl md:text-5xl font-bold text-white mb-3 md:mb-4 leading-tight">Daiane Borges</h3>
                  <div className="w-16 h-[2px] bg-gradient-to-r from-[#D4AF37] to-transparent mb-6 md:mb-8" />
                  <p className="text-sm md:text-base text-gray-300 leading-relaxed mb-4 md:mb-6">
                    Sócia proprietária e fundadora do Instituto DB, esteticista e cosmetóloga, pós-graduada em estética avançada e especialista em protocolos e tecnologias de alta performance.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Serviços */}
      <section id="servicos" className="py-16 md:py-32 px-4 md:px-6 bg-black">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-16">
            <h2 data-animate="fade-up" className="font-serif text-3xl md:text-4xl font-bold text-white mb-4 md:mb-6 leading-tight">Áreas de Atuação</h2>
            <div data-animate="fade" data-delay="200" className="w-16 md:w-24 h-[2px] bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent mx-auto mb-6 md:mb-8" />
            <p data-animate="fade-up" data-delay="300" className="text-base md:text-xl text-[#D4AF37] font-light">Excelência em Estética e Saúde</p>
          </div>

          <div className="mb-12 md:mb-20">
            <h3 data-animate="fade-left" className="text-xs md:text-sm font-bold tracking-[4px] text-[#D4AF37] mb-8 md:mb-12 uppercase">Tratamentos</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6">
              {TREATMENTS.map((item) => (
                <div
                  key={item.title}
                  data-animate="zoom"
                  className="bg-white border-t-[3px] border-[#D4AF37] rounded-2xl p-5 md:p-6 flex flex-col items-center text-center hover:-translate-y-1 transition-all duration-300 hover:shadow-xl hover:shadow-[#D4AF37]/20 group"
                >
                  <div className="w-10 h-10 flex items-center justify-center mb-3">
                    <i className={`${item.icon} text-2xl text-[#D4AF37] group-hover:scale-110 transition-transform duration-300`} />
                  </div>
                  <p className="text-xs md:text-sm text-black font-medium leading-snug">{item.title}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 data-animate="fade-left" className="text-xs md:text-sm font-bold tracking-[4px] text-[#D4AF37] mb-8 md:mb-12 uppercase">Saúde</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-8">
              {HEALTH_SERVICES.map((service, index) => (
                <div
                  key={service.title}
                  data-animate="fade-up"
                  data-delay={`${index * 100}`}
                  className="bg-white rounded-2xl md:rounded-3xl p-6 md:p-10 border-t-[3px] border-[#D4AF37] hover:-translate-y-2 transition-all duration-300 hover:shadow-2xl hover:shadow-[#D4AF37]/20 group"
                >
                  <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center mb-4 md:mb-6">
                    <i className={`${service.icon} text-3xl md:text-4xl text-[#D4AF37] group-hover:scale-110 transition-transform duration-300`} />
                  </div>
                  <h4 className="font-semibold text-lg md:text-xl text-black mb-3 md:mb-4">{service.title}</h4>
                  <div className="w-10 h-[1px] bg-[#D4AF37] mb-4 md:mb-5" />
                  <p className="text-xs md:text-sm text-gray-600 leading-relaxed">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contato */}
      <section id="contato" className="py-16 md:py-32 px-4 md:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-16">
            <h2 data-animate="fade-up" className="font-serif text-3xl md:text-5xl font-bold text-black mb-4 md:mb-6 leading-tight">Entre em Contato</h2>
            <div data-animate="fade" data-delay="200" className="w-16 md:w-24 h-[2px] bg-[#D4AF37] mx-auto mb-6 md:mb-8" />
            <p data-animate="fade-up" data-delay="300" className="text-base md:text-lg text-gray-700">Estamos prontos para atendê-lo</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 md:gap-16 items-start">
            <div data-animate="fade-left">
              <div className="bg-[#FAFAFA] border border-[#D4AF37] rounded-2xl p-6 md:p-10">
                <form
                  ref={formRef}
                  id="contact-form"
                  className="space-y-5 md:space-y-6"
                  onSubmit={handleFormSubmit}
                >
                  <input type="text"  name="nome"     placeholder="Nome Completo" required className="w-full h-12 bg-transparent border-b border-[#D4AF37] text-black placeholder-gray-400 focus:outline-none text-sm" />
                  <input type="tel"   name="telefone" placeholder="Telefone"      required className="w-full h-12 bg-transparent border-b border-[#D4AF37] text-black placeholder-gray-400 focus:outline-none text-sm" />
                  <input type="email" name="email"    placeholder="E-mail"        required className="w-full h-12 bg-transparent border-b border-[#D4AF37] text-black placeholder-gray-400 focus:outline-none text-sm" />
                  <textarea name="mensagem" placeholder="Mensagem" rows={4} maxLength={500} required className="w-full bg-transparent border-b border-[#D4AF37] text-black placeholder-gray-400 focus:outline-none resize-none text-sm pt-2" />

                  {formStatus === 'ok' && (
                    <p className="text-sm text-green-600 font-medium">✓ Mensagem enviada com sucesso! Entraremos em contato em breve.</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-500 font-medium">✗ Erro ao enviar. Verifique sua conexão e tente novamente.</p>
                  )}
                  {formStatus === 'cooldown' && (
                    <div className="flex items-center gap-2 bg-[#D4AF37]/10 border border-[#D4AF37]/40 rounded-lg px-4 py-3">
                      <i className="ri-time-line text-[#D4AF37] text-lg flex-shrink-0" />
                      <p className="text-sm text-gray-700">
                        Aguarde <span className="font-bold text-[#D4AF37]">{fmtCooldown(cooldownSeconds)}</span> para enviar outra mensagem.
                      </p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === 'sending' || formStatus === 'cooldown'}
                    className="w-full h-12 bg-black text-white text-sm font-medium rounded-full border border-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {formStatus === 'sending' ? 'Enviando...' : formStatus === 'cooldown' ? `Aguarde ${fmtCooldown(cooldownSeconds)}` : 'Enviar Mensagem'}
                  </button>
                </form>
              </div>
            </div>

            <div data-animate="fade-right" className="flex flex-col gap-6 md:gap-8">
              {/* Telefone */}
              <div className="bg-[#FAFAFA] border border-[#D4AF37] rounded-2xl p-6 md:p-8 flex items-start gap-5">
                <div className="w-12 h-12 md:w-14 md:h-14 flex-shrink-0 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#F4E5B8] flex items-center justify-center">
                  <i className="ri-phone-line text-xl md:text-2xl text-black" />
                </div>
                <div>
                  <h3 className="font-semibold text-base md:text-lg text-black mb-1">Telefone</h3>
                  <p className="text-sm md:text-base text-[#D4AF37] mb-3">(51) 99481-5792</p>
                  <a
                    href="tel:+5551994815792"
                    className="inline-block px-5 py-2 bg-black text-white text-sm font-medium rounded-full border border-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Ligar Agora
                  </a>
                </div>
              </div>

              {/* Horário */}
              <div className="bg-[#FAFAFA] border border-[#D4AF37] rounded-2xl p-6 md:p-8 flex items-start gap-5">
                <div className="w-12 h-12 md:w-14 md:h-14 flex-shrink-0 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#F4E5B8] flex items-center justify-center">
                  <i className="ri-time-line text-xl md:text-2xl text-black" />
                </div>
                <div>
                  <h3 className="font-semibold text-base md:text-lg text-black mb-2">Horário de Funcionamento</h3>
                  <div className="space-y-1 text-sm text-gray-600">
                    <p>Segunda a Sexta: 9:00 às 12:00 | 13:30 às 18:00</p>
                    <p>Sábado: Fechado</p>
                    <p>Domingo: Fechado</p>
                  </div>
                </div>
              </div>

              {/* Endereço */}
              <div className="bg-[#FAFAFA] border border-[#D4AF37] rounded-2xl p-6 md:p-8 flex flex-col gap-4">
                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 md:w-14 md:h-14 flex-shrink-0 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#F4E5B8] flex items-center justify-center">
                    <i className="ri-map-pin-line text-xl md:text-2xl text-black" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-base md:text-lg text-black mb-1">Endereço</h3>
                    <p className="text-sm text-gray-600">
                      Av. Central, 1990 - Zona Nova<br />
                      Capão da Canoa - RS
                    </p>
                  </div>
                </div>
                <div className="w-full h-44 md:h-52 rounded-xl border border-[#D4AF37] overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3467.0!2d-50.0!3d-29.75!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sAv.%20Central%2C%201990%20-%20Zona%20Nova%2C%20Cap%C3%A1o%20da%20Canoa%20-%20RS!5e0!3m2!1spt-BR!2sbr!4v1704067200000!5m2!1spt-BR!2sbr&q=Av.+Central,+1990,+Zona+Nova,+Cap%C3%A1o+da+Canoa,+RS,+Brasil"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Localização Instituto Daiane Borges"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WhatsApp flutuante */}
      <a
        href="https://wa.me/5551994815792"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-4 md:bottom-8 md:right-6 z-50 flex items-center gap-3 cursor-pointer group"
        aria-label="Falar no WhatsApp"
      >
        <span className="opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-2 group-hover:translate-x-0 bg-black text-white text-xs font-medium px-3 py-1.5 rounded-full whitespace-nowrap">
          Falar no WhatsApp
        </span>
        <div
          className="relative w-11 h-11 md:w-14 md:h-14 rounded-full bg-[#25D366] flex items-center justify-center shadow-2xl hover:scale-110 transition-transform duration-300"
          style={{ boxShadow: '0 4px 24px rgba(37,211,102,0.45)' }}
        >
          <i className="ri-whatsapp-line text-2xl md:text-3xl text-white relative z-10" />
        </div>
      </a>

      {/* Footer */}
      <footer className="bg-black border-t border-[#D4AF37] py-12 md:py-20 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 mb-10 md:mb-12">
            <div data-animate="fade-up">
              <div className="flex items-center gap-2 mb-4">
                <img
                  src="https://static.readdy.ai/image/eec107a1548fe8d7ef2a6c045cb2b024/07f00f65d65583c55d51b986d8655c28.png"
                  alt="Instituto Daiane Borges"
                  className="h-9 md:h-10 w-auto"
                />
              </div>
              <h3 className="font-serif text-lg md:text-xl text-white mb-3">Instituto Daiane Borges</h3>
              <div className="w-10 h-[2px] bg-[#D4AF37] mb-4" />
              <p className="text-sm text-gray-500 leading-relaxed">
                Excelência em saúde e estética com atendimento humanizado e resultados transformadores.
              </p>
            </div>

            <div data-animate="fade-up" data-delay="100">
              <h4 className="text-sm md:text-base font-semibold text-white mb-4 md:mb-6">Links Rápidos</h4>
              <div className="space-y-2 md:space-y-3">
                {NAV_ITEMS.map((item) => (
                  <a key={item.id} href={`#${item.id}`} className="block text-sm text-gray-400 hover:text-[#D4AF37] transition-colors capitalize">
                    {item.label}
                  </a>
                ))}
              </div>
            </div>

            <div data-animate="fade-up" data-delay="200">
              <h4 className="text-sm md:text-base font-semibold text-white mb-4 md:mb-6">Contato</h4>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <i className="ri-phone-line text-[#D4AF37] flex-shrink-0" />
                  <span className="text-sm text-gray-400">(51) 99481-5792</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-mail-line text-[#D4AF37] flex-shrink-0" />
                  <span className="text-xs text-gray-400 whitespace-nowrap">contato@institutodaianeborges.com</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-map-pin-line text-[#D4AF37] mt-1 flex-shrink-0" />
                  <span className="text-sm text-gray-400">Av. Central, 1990 - Zona Nova<br />Capão da Canoa - RS</span>
                </div>
              </div>
            </div>

            <div data-animate="fade-up" data-delay="300">
              <h4 className="text-sm md:text-base font-semibold text-white mb-4 md:mb-6">Siga-nos</h4>
              <div className="flex gap-4">
                <a
                  href="https://www.instagram.com/institutodbestetica?igsh=MTdnYTNtZ2VocjdrNQ=="
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-11 h-11 md:w-12 md:h-12 rounded-full border-2 border-[#D4AF37] flex items-center justify-center hover:bg-[#D4AF37] transition-all duration-300 group cursor-pointer"
                >
                  <i className="ri-instagram-line text-lg md:text-xl text-[#D4AF37] group-hover:text-black" />
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-[#D4AF37] pt-6 md:pt-8">
            <p className="text-xs md:text-sm text-gray-500 text-center">© 2025 Instituto Daiane Borges. Todos os direitos reservados.</p>
            <p className="text-xs text-gray-600 text-center mt-2">© Developed by <a href="https://www.instagram.com/ronaldobdev/" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] font-medium hover:underline cursor-pointer">Ronaldo B Dev</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
